import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { UserPlus, Search, Handshake, Sparkles, ArrowRight } from "lucide-react";

const steps = [
    {
        icon: UserPlus,
        step: "01",
        title: "Create Your Profile",
        description:
            "Sign up and build your civic profile. Share your experience, interests, and how you want to contribute.",
        color: "accent",
    },
    {
        icon: Search,
        step: "02",
        title: "Discover Opportunities",
        description:
            "Browse events, volunteer opportunities, channels, and connections tailored to your interests.",
        color: "primary",
    },
    {
        icon: Handshake,
        step: "03",
        title: "Connect & Collaborate",
        description:
            "Engage with like-minded civic professionals, join discussions, and participate in projects.",
        color: "accent",
    },
    {
        icon: Sparkles,
        step: "04",
        title: "Make an Impact",
        description:
            "Track your contributions, earn recognition, and see the difference you're making.",
        color: "primary",
    },
];

export const HowItWorksSection = () => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
        <section id="how-it-works" className="section-padding relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-muted/10 to-muted/30" />

            <div className="container mx-auto relative z-10" ref={ref}>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6 }}
                    className="text-center max-w-3xl mx-auto mb-16"
                >
                    <motion.span
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={isInView ? { opacity: 1, scale: 1 } : {}}
                        transition={{ duration: 0.4, delay: 0.1 }}
                        className="inline-block text-primary font-semibold text-sm uppercase tracking-widest mb-4 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20"
                    >
                        How It Works
                    </motion.span>
                    <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                        Your Journey to{" "}
                        <span className="gradient-text-accent">Civic Engagement</span>
                    </h2>
                    <p className="text-muted-foreground text-lg">
                        Get started in minutes and begin making an impact in your community
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {steps.map((step, index) => (
                        <motion.div
                            key={step.step}
                            initial={{ opacity: 0, y: 30 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                            className="relative group"
                        >
                            {index < steps.length - 1 && (
                                <div className="hidden lg:block absolute top-10 left-[60%] right-[-40%] h-[2px]">
                                    <motion.div
                                        initial={{ scaleX: 0 }}
                                        animate={isInView ? { scaleX: 1 } : {}}
                                        transition={{ duration: 0.6, delay: 0.5 + index * 0.15 }}
                                        className="h-full bg-gradient-to-r from-accent/40 to-primary/20 origin-left"
                                    />
                                    <ArrowRight className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 h-4 w-4 text-primary/40" />
                                </div>
                            )}

                            <motion.div
                                whileHover={{ y: -6 }}
                                transition={{ duration: 0.3 }}
                                className="glass-card rounded-2xl p-6 h-full hover:shadow-lg transition-all duration-300 hover:border-accent/30"
                            >
                                <div className="flex items-start gap-4 mb-4">
                                    <motion.div
                                        whileHover={{ scale: 1.1, rotate: 5 }}
                                        className={`w-14 h-14 rounded-xl flex-shrink-0 flex items-center justify-center shadow-lg ${step.color === "accent"
                                                ? "bg-gradient-to-br from-accent to-accent/80 text-accent-foreground"
                                                : "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground"
                                            }`}
                                    >
                                        <step.icon className="h-6 w-6" />
                                    </motion.div>
                                    <span className="font-display text-4xl font-bold text-muted-foreground/20 group-hover:text-muted-foreground/30 transition-colors">
                                        {step.step}
                                    </span>
                                </div>
                                <h3 className="font-display font-semibold text-lg mb-3 text-foreground">
                                    {step.title}
                                </h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    {step.description}
                                </p>
                            </motion.div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
